import React, { useState } from "react";
import "./App.css";

const skills = {
  languages: [
    "Java",
    "Python",
    "SQL",
    "JavaScript",
    "HTML/CSS",
    "C",
    "C++",
    "Golang",
    "Rust",
    "TypeScript",
    "Swift",
    "PHP",
    "Ruby",
  ],
  technologies: [
    "ReactJS",
    "AWS",
    "Docker",
    "Django",
    "Firebase",
    "MongoDB",
    "PostgreSQL",
    "Angular",
    "Android",
    "Spring Boot",
  ],
};

const projects = [
  {
    title: "Electricity Prices Predictor",
    description:
      "Built a machine learning-powered prediction tool achieving 70% accuracy by training models on 1M+ rows of data using Google Colab.",
    technologies: ["Go", "Python", "Machine Learning", "Google Colab"],
    github: "https://github.com/SohaibDM/Comp333",
    highlights: [
      "70% prediction accuracy",
      "1M+ data rows processed",
      "Automated data pipeline",
    ],
  },
  {
    title: "Tennis Analyzer",
    description:
      "Developed a tennis video analysis tool using Python and YOLO, achieving 90% accuracy in object detection and analyzing 100+ matches.",
    technologies: ["Python", "YOLO", "Computer Vision", "Google Colab"],
    github: "https://github.com/SohaibDM/Tennis_Analyzer",
    highlights: [
      "90% object detection accuracy",
      "100+ matches analyzed",
      "50% faster analysis time",
    ],
  },
  {
    title: "COSN: Community Social Media Network",
    description:
      "Developed a full-stack social media replica with user authentication, role-based access control, messaging, and group interactions.",
    technologies: ["PHP", "MySQL", "JavaScript", "HTML/CSS"],
    github: "https://github.com/SohaibDM/COMP353",
    highlights: [
      "Real-time messaging",
      "Role-based access control",
      "Dynamic interactions",
    ],
  },
  {
    title: "Sorting Algorithms Visualizer",
    description:
      "Developed an interactive visualization tool for 4 sorting algorithms with real-time, color-coded animations.",
    technologies: ["Python", "Pygame"],
    github: "https://github.com/SohaibDM/Sorting-Algorithms-Visualizer",
    highlights: [
      "4 sorting algorithms",
      "Real-time visualization",
      "Customizable interface",
    ],
  },
  {
    title: "Tower Defense Game",
    description:
      "Led a team of 3 developers to deliver a Tower Defense game implementing 4 design patterns for maintainable architecture.",
    technologies: ["C++", "SFML", "Design Patterns"],
    github:
      "https://github.com/FadyRizkalla/Comp345_Groupe_project/tree/main",
    highlights: [
      "Team leadership",
      "4 design patterns",
      "Maintainable architecture",
    ],
  },
  {
    title: "FitoTrack Contributions",
    description:
      "Developed workout history management features with robust unit tests and optimized database interactions.",
    technologies: ["Android", "Kotlin"],
    github: "https://codeberg.org/COMP354Winter2025/FitoTrackSki",
    highlights: [
      "Open source contribution",
      "Unit testing",
      "Database optimization",
    ],
  },
];

const experiences = [
  {
    company: "Lightspeed Commerce",
    position: "Software Developer Intern - X-Series, Services Team",
    duration: "May 2025 – Sep. 2025",
    location: "Montreal, QC",
    technologies: ["React", "TypeScript", "Go", "GraphQL"],
    logo: require("./images/Lightspeed_Commerce_Inc__Lightspeed_Commerce_Responds_to_Media_R.jpg"),
    achievements: [
      "Built Services creation feature for Lightspeed’s flagship X-Series using React, Go, and GraphQL",
      "Developed Python automation framework for API testing, ensuring full coverage and faster releases",
      "Monitored production with Datadog and Raygun for deployment to 1400+ retailers",
    ],
  },
  {
    company: "Hexploit Alliance",
    position: "Full-stack Developer",
    duration: "Aug. 2024 – Feb. 2025",
    location: "Montreal, QC",
    technologies: ["Python", "Django", "ReactJS", "WebSocket"],
    logo: require("./images/hexploit.png"),
    achievements: [
      "Built online multiplayer trivia game for 600+ participants using WebSockets",
      "Engineered Django backend with WebSocket integration for real-time game state",
      "Built 3 responsive React apps with Reducer state management and Markdown support",
    ],
  },
];


const galleryFolders = [
  {
    name: "Lightspeed",
    folder: "Lightspeed",
    images: [
      require("./images/Figma/Lightspeed/1.png"),
      require("./images/Figma/Lightspeed/2.png"),
      require("./images/Figma/Lightspeed/3.png"),
    ],
    cover: require("./images/Figma/Lightspeed/1.png"),
  },
  {
    name: "Prayer Times Screen",
    folder: "PrayerTimes",
    images: [
      require("./images/Figma/PrayerTimes/DayTime.png"),
      require("./images/Figma/PrayerTimes/NightTime.png"),
      require("./images/Figma/PrayerTimes/SunRise.png"),
      require("./images/Figma/PrayerTimes/SunSet.png"),
    ],
    cover: require("./images/Figma/PrayerTimes/DayTime.png"),
  },
  {
    name: "Trivia Game",
    folder: "Game",
    images: [
      require("./images/Figma/Game/Board.png"),
      require("./images/Figma/Game/Board2.png"),
      require("./images/Figma/Game/PhoneDark.png"),
      require("./images/Figma/Game/PhoneLight.png"),
    ],
    cover: require("./images/Figma/Game/Board.png"),
  },
];

const App = () => {
  const [navOpen, setNavOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeGallery, setActiveGallery] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 overflow-x-hidden">
      {/* Navigation */}
      <nav className="bg-gray-950 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="text-lg sm:text-xl font-semibold text-white">
              Sohaib Daami
            </div>
            {/* Hamburger for mobile */}
            <button
              className="md:hidden text-gray-400 hover:text-white focus:outline-none p-2 -mr-2"
              onClick={() => setNavOpen(!navOpen)}
              aria-label={navOpen ? "Close menu" : "Open menu"}
            >
              <svg
                className="w-6 h-6 sm:w-7 sm:h-7"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {navOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
            <div className="hidden md:flex space-x-6 lg:space-x-8">
              <a
                href="#about"
                className="text-gray-400 hover:text-white transition-colors text-sm lg:text-base"
              >
                About
              </a>
              <a
                href="#experience"
                className="text-gray-400 hover:text-white transition-colors text-sm lg:text-base"
              >
                Experience
              </a>
              <a
                href="#projects"
                className="text-gray-400 hover:text-white transition-colors text-sm lg:text-base"
              >
                Projects
              </a>
              <a
                href="#skills"
                className="text-gray-400 hover:text-white transition-colors text-sm lg:text-base"
              >
                Skills
              </a>
              <a
                href="#contact"
                className="text-gray-400 hover:text-white transition-colors text-sm lg:text-base"
              >
                Contact
              </a>
            </div>
          </div>
          {/* Mobile nav */}
          {navOpen && (
            <div className="md:hidden bg-gray-950 border-t border-gray-800">
              <div className="flex flex-col space-y-1 px-2 py-3">
                <a
                  href="#about"
                  className="text-gray-400 hover:text-white hover:bg-gray-800 transition-colors px-3 py-2 rounded-md text-base"
                  onClick={() => setNavOpen(false)}
                >
                  About
                </a>
                <a
                  href="#experience"
                  className="text-gray-400 hover:text-white hover:bg-gray-800 transition-colors px-3 py-2 rounded-md text-base"
                  onClick={() => setNavOpen(false)}
                >
                  Experience
                </a>
                <a
                  href="#projects"
                  className="text-gray-400 hover:text-white hover:bg-gray-800 transition-colors px-3 py-2 rounded-md text-base"
                  onClick={() => setNavOpen(false)}
                >
                  Projects
                </a>
                <a
                  href="#skills"
                  className="text-gray-400 hover:text-white hover:bg-gray-800 transition-colors px-3 py-2 rounded-md text-base"
                  onClick={() => setNavOpen(false)}
                >
                  Skills
                </a>
                <a
                  href="#contact"
                  className="text-gray-400 hover:text-white hover:bg-gray-800 transition-colors px-3 py-2 rounded-md text-base"
                  onClick={() => setNavOpen(false)}
                >
                  Contact
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>


      {/* Hero Section */}
      <section
        id="about"
        className="relative py-8 sm:py-12 lg:py-20 bg-gray-900"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="flex flex-col sm:flex-row items-center sm:items-start mb-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden mb-4 sm:mb-0 sm:mr-6 border-2 border-gray-200 flex-shrink-0">
                  <img
                    src={require("./images/DSC09664.JPG")}
                    alt="Sohaib Daami"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center sm:text-left">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
                    Sohaib Daami
                  </h1>
                  <p className="text-lg sm:text-xl text-gray-400">
                    Full-Stack Developer
                  </p>
                </div>
              </div>
              <p className="text-base sm:text-lg text-gray-300 mb-6 leading-relaxed text-center sm:text-left">
                Software Developer at Lightspeed Commerce with hands-on
                experience building scalable applications and contributing to
                open source projects. Computer Science student at Concordia
                University.
              </p>
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 sm:gap-4 mb-6 sm:mb-8">
                <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-green-900 text-green-200">
                  📍 Montreal, QC, Canada
                </span>
                <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-900 text-blue-200">
                  🎓 Top 15% - Dean's List 
                </span>
                <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-purple-900 text-purple-200">
                  👨‍🏫 Teaching Assistant
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center sm:justify-start">
                <a
                  href="https://github.com/SohaibDM"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-4 sm:px-6 py-3 border border-gray-700 rounded-md shadow-sm bg-gray-950 text-gray-200 hover:bg-gray-800 transition-colors text-sm sm:text-base"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                  GitHub
                </a>
                <a
                  href="http://linkedin.com/in/sohaib-daami-318059304"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 transition-colors text-sm sm:text-base"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </a>
              </div>
            </div>
            <div className="relative order-1 lg:order-2 mt-8 lg:mt-0">
              <img
                src={require("./images/curry.jpg")}
                alt="Basketball shooting - reaching for excellence"
                className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-full mx-auto rounded-lg object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Design Gallery Section */}
      <section id="design-gallery" className="py-12 sm:py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 sm:mb-12 text-center">
            Design Gallery
          </h2>
          <div className="flex flex-row gap-8 justify-center mx-auto">
            {galleryFolders.map((folder, idx) => (
              <button
                key={folder.name}
                className="group bg-gray-900 border border-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow focus:outline-none"
                style={{ width: '60rem', maxWidth: '95vw' }}
                onClick={() => {
                  setActiveGallery(idx);
                  setActiveImage(0);
                  setGalleryOpen(true);
                }}
                aria-label={`Open gallery for ${folder.name}`}
              >
                <div className="relative h-56 sm:h-64 flex items-center justify-center bg-gray-800">
                  <img
                    src={folder.cover}
                    alt={folder.name + " cover"}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-200"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                    <span className="text-xl sm:text-2xl font-semibold text-white tracking-wide">
                      {folder.name}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Modal/Lightbox */}
        {galleryOpen && activeGallery !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
            <div className="relative max-w-2xl w-full mx-4">
              <button
                className="absolute top-2 right-2 text-gray-300 hover:text-white text-3xl font-bold focus:outline-none"
                onClick={() => setGalleryOpen(false)}
                aria-label="Close gallery"
              >
                &times;
              </button>
              <div className="flex flex-col items-center">
                <img
                  src={galleryFolders[activeGallery].images[activeImage]}
                  alt={galleryFolders[activeGallery].name + " " + (activeImage + 1)}
                  className="rounded-lg max-h-[70vh] w-auto mb-4 shadow-lg border border-gray-700 bg-gray-900"
                  style={{ maxWidth: '100%' }}
                />
                <div className="flex gap-2 mb-4">
                  {galleryFolders[activeGallery].images.map((img, i) => (
                    <button
                      key={i}
                      className={`w-16 h-12 rounded border-2 ${i === activeImage ? 'border-blue-500' : 'border-gray-700'} overflow-hidden focus:outline-none`}
                      onClick={() => setActiveImage(i)}
                      aria-label={`Show image ${i + 1}`}
                    >
                      <img src={img} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                <div className="flex justify-between w-full">
                  <button
                    className="px-4 py-2 bg-gray-800 text-gray-200 rounded hover:bg-gray-700 disabled:opacity-50"
                    onClick={() => setActiveImage((prev) => Math.max(prev - 1, 0))}
                    disabled={activeImage === 0}
                  >
                    Previous
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-800 text-gray-200 rounded hover:bg-gray-700 disabled:opacity-50"
                    onClick={() => setActiveImage((prev) => Math.min(prev + 1, galleryFolders[activeGallery].images.length - 1))}
                    disabled={activeImage === galleryFolders[activeGallery].images.length - 1}
                  >
                    Next
                  </button>
                </div>
                <div className="mt-4 text-gray-400 text-sm">
                  {galleryFolders[activeGallery].name} — Image {activeImage + 1} of {galleryFolders[activeGallery].images.length}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-12 sm:py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 sm:mb-12 text-center">
            Professional Experience
          </h2>
          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className="bg-gray-900 rounded-lg border border-gray-800 p-6 sm:p-8 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <img
                      src={exp.logo}
                      alt={`${exp.company} logo`}
                      className="w-16 h-16 object-contain rounded-md border border-gray-700 self-center sm:self-auto"
                    />
                    <div className="text-center sm:text-left">
                      <h3 className="text-lg sm:text-xl font-semibold text-white">
                        {exp.position}
                      </h3>
                      <p className="text-base sm:text-lg text-blue-400 font-medium">
                        {exp.company}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 lg:mt-0 text-center lg:text-right">
                    <p className="text-sm sm:text-base text-gray-400">
                      {exp.duration}
                    </p>
                    <p className="text-sm sm:text-base text-gray-500">
                      {exp.location}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-4 justify-center sm:justify-start">
                  {exp.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-gray-200"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <ul className="space-y-2">
                  {exp.achievements.map((achievement, achIndex) => (
                    <li key={achIndex} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-green-400 mt-0.5 mr-3 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm sm:text-base text-gray-300">
                        {achievement}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-12 sm:py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 sm:mb-12 text-center">
            Featured Projects
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="bg-gray-950 border border-gray-800 rounded-lg overflow-hidden hover:shadow-lg transition-shadow group"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xl font-semibold text-white group-hover:text-blue-400 transition-colors hover:underline"
                    >
                      {project.title}
                    </a>
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                      </svg>
                    </a>
                  </div>
                  <p className="text-gray-400 mb-4 text-sm leading-relaxed">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-900 text-blue-200"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="space-y-1">
                    {project.highlights.map((highlight, highlightIndex) => (
                      <div
                        key={highlightIndex}
                        className="flex items-center text-sm text-gray-400"
                      >
                        <svg
                          className="w-4 h-4 text-green-400 mr-2 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-12 sm:py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 sm:mb-12 text-center">
            Technical Skills
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            <div className="bg-gray-900 rounded-lg border border-gray-800 p-8">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                <svg
                  className="w-6 h-6 text-blue-400 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
                Programming Languages
              </h3>
              <div className="flex flex-wrap gap-3">
                {skills.languages.map((language, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700 transition-colors"
                  >
                    {language}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-gray-900 rounded-lg border border-gray-800 p-8">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                <svg
                  className="w-6 h-6 text-green-400 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                  />
                </svg>
                Technologies & Frameworks
              </h3>
              <div className="flex flex-wrap gap-3">
                {skills.technologies.map((tech, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700 transition-colors"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 sm:py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Get In Touch</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              I'm always interested in new opportunities and collaborations.
              Feel free to reach out if you'd like to work together!
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center p-6 bg-gray-800 rounded-lg">
              <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Email</h3>
              <a
                href="mailto:sohaibdaami00@gmail.com"
                className="text-blue-400 hover:text-blue-200 transition-colors"
              >
                sohaibdaami00@gmail.com
              </a>
            </div>
            <div className="text-center p-6 bg-gray-800 rounded-lg">
              <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Phone</h3>
              <a
                href="tel:+15146605926"
                className="text-blue-400 hover:text-blue-200 transition-colors"
              >
                +1 (514) 660-5926
              </a>
            </div>
            <div className="text-center p-6 bg-gray-800 rounded-lg">
              <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Location</h3>
              <span className="text-gray-400">Montreal, QC, Canada</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-gray-400 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-500">
                © 2025 Sohaib Daami. All rights reserved.
              </p>
            </div>
            <div className="flex space-x-6">
              <a
                href="https://github.com/SohaibDM"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <span className="sr-only">GitHub</span>
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
              </a>
              <a
                href="http://linkedin.com/in/sohaib-daami-318059304"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <span className="sr-only">LinkedIn</span>
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
